/**
 * Skills e MCPs on the web (docs/refactor/14-skills-e-mcps.md): what each of the person's computers
 * has installed, as its desktop app told Vephus — and installing on one of them from here: the
 * catalog's install prompt, sent to that computer only (the queue's target machine), where the
 * computer's own AI does it.
 */

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Download, Loader2, Monitor } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { usePromptCatalog } from '../lib/prompts'
import { checkRepoUrl, fillUrl } from '../lib/skillsAndMcps'
import { Badge, EmptyState, Panel, Skeleton } from './primitives'
import type { DeveloperMachine } from '../lib/types'

export function MachinesInventory({ kind }: { kind: 'skills' | 'mcps' }) {
  const { t, locale } = useI18n()
  const { data: machines, isLoading } = useQuery({ queryKey: ['machines'], queryFn: api.promptQueue.machines, refetchInterval: 30_000, retry: false })

  return (
    <Panel title={t('inventory.title')} subtitle={t(kind === 'skills' ? 'inventory.hintSkills' : 'inventory.hintMcps')}>
      {isLoading ? (
        <Skeleton height={80} />
      ) : !machines?.length ? (
        <EmptyState title={t('machines.none')} body={t('machines.noneHint')} />
      ) : (
        <ul className="machines">
          {machines.map((machine) => {
            const items = (kind === 'skills' ? machine.skills : machine.mcps) ?? null
            return (
              <li key={machine.id} className={`machines__row${machine.online ? ' is-on' : ''}`}>
                <div className="row row--tight row--wrap">
                  <Monitor size={14} aria-hidden />
                  <strong className="small">{machine.name ?? t('machines.unnamed')}</strong>
                  <span className="tiny muted">{machine.online ? t('machines.on') : t('machines.seen', { when: formatRelative(machine.lastSeenAt, locale) })}</span>
                  {machine.inventoryAt && <span className="tiny muted">· {t('inventory.toldAt', { when: formatRelative(machine.inventoryAt, locale) })}</span>}
                </div>
                {items === null ? (
                  <span className="tiny muted">{t('inventory.notYet')}</span>
                ) : items.length === 0 ? (
                  <span className="tiny muted">{t(kind === 'skills' ? 'inventory.noSkills' : 'inventory.noMcps')}</span>
                ) : (
                  <div className="row row--tight row--wrap">
                    {items.map((item) => (
                      <span key={`${item.scope ?? ''}:${item.name}`} className={`inventory__item${item.on ? '' : ' is-off'}`} title={item.on ? t('inventory.on') : t('inventory.off')}>
                        <span className="mono tiny">{item.name}</span>
                        {item.scope === 'project' && <Badge>{t('inventory.project')}</Badge>}
                        {!item.on && <span className="tiny muted">{t('inventory.off')}</span>}
                      </span>
                    ))}
                  </div>
                )}
                {machine.online && <InstallHere machine={machine} kind={kind} />}
              </li>
            )
          })}
        </ul>
      )}
    </Panel>
  )
}

/** "Instalar neste computador": a repository's address; the catalog's prompt goes to that computer only. */
function InstallHere({ machine, kind }: { machine: DeveloperMachine; kind: 'skills' | 'mcps' }) {
  const { t } = useI18n()
  const { data: catalog } = usePromptCatalog()
  const [url, setUrl] = useState('')
  const [done, setDone] = useState(false)
  const install = useMutation({
    mutationFn: async () => {
      const prompt = catalog?.prompts.find((p) => p.key === (kind === 'skills' ? 'skills.install-from-repo' : 'mcp.install-from-repo'))
      if (!prompt) throw new Error('catalog-missing')
      return api.promptQueue.send(fillUrl(prompt.body, url), null, 1, machine.id)
    },
    onSuccess: () => {
      setUrl('')
      setDone(true)
    },
  })
  const check = url.trim() ? checkRepoUrl(url) : 'ok'
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setDone(false)
    if (url.trim() && check === 'ok') install.mutate()
  }
  return (
    <form className="row row--tight row--wrap inventory__install" onSubmit={submit}>
      <input
        className="input input--sm"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder={t(kind === 'skills' ? 'inventory.installSkillPlaceholder' : 'inventory.installMcpPlaceholder')}
        aria-label={t('inventory.installHere', { name: machine.name ?? '' })}
      />
      <button type="submit" className="btn btn--sm" disabled={!url.trim() || check !== 'ok' || install.isPending}>
        {install.isPending ? <Loader2 size={13} className="spin" /> : <Download size={13} />} {t('inventory.installHere', { name: machine.name ?? '' })}
      </button>
      {check !== 'ok' && <span className="tiny run-bar__notice--bad">{t(check === 'credentials' ? 'skills.error.url_has_credentials' : 'skills.error.invalid_url')}</span>}
      {done && <span className="tiny muted">{t('inventory.sent', { name: machine.name ?? '' })}</span>}
      {install.isError && <span className="tiny run-bar__notice--bad">{t('error.generic')}</span>}
    </form>
  )
}
